import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { Tasks, Asker } from 'common/src/utils/types';
import { fetchCommentsForTask } from 'common/src/api/comments';
import { getAskerAddress } from 'common/src/api/askers_addresses';
import { updateTask } from 'common/src/api/tasks';
import { getAskerById } from 'common/src/api/askers';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa'; // Add this import for the WhatsApp icon
import useUserStore from '../../../store/useUserStore';
import LeftColumn from './LeftColumn';
import RightColumn from './RightColumn';
import { ModalAddComment } from './modalAddComment';
import DatePicker from './DatePicker';
import { Comment } from './types';
import { cleanPhoneNumber } from 'common/src/utils/functions';
import { fetchOneCategory, getCategories } from 'common/src/api/categories';
import { fetchAvailableTaskers } from 'common/src/api/taskers';

interface ModalTaskProps {
  isOpen: boolean;
  onClose: () => void;
  fetchTasks?: () => Promise<void>;
  task: Tasks;
  onReset: () => void;
}

interface Category {
  id: number;
  name: string;
}

interface Tasker {
  id: number;
  email: string;
}

const ModalTask: React.FC<ModalTaskProps> = ({
  isOpen,
  onClose,
  task: initialTask,
  onReset,
  fetchTasks,
}) => {
  const { userProfile } = useUserStore();
  const [task, setTask] = useState<Tasks>(initialTask);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [taskDate, setTaskDate] = useState(task?.date || '');
  const [askerAddress, setAskerAddress] = useState('');
  const [askerCommune, setAskerCommune] = useState('');
  const [asker, setAsker] = useState<Asker | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [taskers, setTaskers] = useState<Tasker[]>([]);
  const [askerCity, setAskerCity] = useState<string>('');
  const [isTaskersLoading, setIsTaskersLoading] = useState(false);

  useEffect(() => {
    // When modal closes, reset everything
    if (!isOpen) {
      setTask(null);
      setTaskers([]);
      setAskerAddress('');
      setAskerCommune('');
      setAskerCity('');
      setCategory(null);
      setIsTaskersLoading(false);
      return;
    }

    // Set the initial task first
    if (initialTask?.id) {
      setTask(initialTask);
    }
  }, [isOpen, initialTask]);

  // Separate useEffect for loading data after task is set
  useEffect(() => {
    if (!task?.id) return;

    console.log('Loading task:', task.id);
    setIsTaskersLoading(true);

    const loadAllData = async () => {
      try {
        console.log('Starting to load address...');
        // Load address and get the data
        const addressData = await loadTaskAddress();
        const cityToUse = addressData?.city || '';
        console.log('Address loaded, city is:', cityToUse);

        // Load other data
        await Promise.all([fetchComments(), loadAsker(), fetchAllCategories()]);

        await fetchCategory();

        // Only fetch taskers after we have the address data
        if (task.category_id) {
          // Pass the city directly instead of relying on state
          await fetchTaskers(task.category_id, cityToUse);
        }
      } catch (error) {
        console.error('Error loading task data:', error);
        toast.error('Error al cargar los datos de la tarea');
      } finally {
        setIsTaskersLoading(false);
      }
    };

    loadAllData();
  }, [task?.id]);

  const fetchComments = async () => {
    if (!task?.id) return [];
    const fetchedComments = await fetchCommentsForTask(task.id.toString());
    setComments(fetchedComments);
    return fetchedComments;
  };

  const fetchCategory = async () => {
    if (task.category_id) {
      try {
        const { data, error } = await fetchOneCategory(task.category_id);
        if (error) throw error;
        setCategory(data);
      } catch (error) {
        console.error('Error fetching category:', error);
        toast.error('Error al cargar la categoría');
      }
    } else {
      setCategory(null);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const { data, error } = await getCategories();
      if (error) throw error;
      setAllCategories(data || []);
    } catch (error) {
      console.error('Error fetching all categories:', error);
      toast.error('Error al cargar todas las categorías');
    }
  };

  const loadTaskAddress = async () => {
    if (!task?.askers_addresses_id) {
      console.log('No address ID found for task');
      setAskerAddress('Sin dirección');
      setAskerCommune('Sin dirección');
      setAskerCity('');
      return null;
    }

    console.log('Loading address for ID:', task.askers_addresses_id);
    setAskerAddress('Cargando...');
    setAskerCommune('Cargando...');
    try {
      const addressData = await getAskerAddress(task.askers_addresses_id);
      if (addressData) {
        console.log('Address loaded:', {
          address: addressData.address,
          commune: addressData.commune,
          city: addressData.city,
        });
        setAskerAddress(addressData.address || 'Sin dirección');
        setAskerCommune(addressData.commune || 'Sin dirección');
        setAskerCity(addressData.city || '');
        return addressData;
      }
    } catch (error) {
      console.error('Error fetching asker address:', error);
      toast.error('Error loading asker address');
      setAskerAddress('Error al cargar');
      setAskerCommune('Error al cargar');
      setAskerCity('');
    }
    return null;
  };

  const loadAsker = async () => {
    if (task?.asker_id) {
      try {
        const askerData = await getAskerById(task.asker_id);
        if (askerData) {
          setAsker(askerData);
        }
      } catch (error) {
        console.error('Error fetching asker:', error);
        toast.error('Error loading asker information');
      }
    }
  };

  const handleAddComment = () => {
    setIsAddCommentModalOpen(true);
  };

  const onCommentAdded = async () => {
    if (task) {
      const updatedComments = await fetchCommentsForTask(task.id.toString());
      setComments(updatedComments);
    }
  };

  const handleDateUpdate = async (newDateTime: string) => {
    try {
      const formattedDate = new Date(newDateTime).toISOString();
      const res = await updateTask(task.id, { date: formattedDate });
      if (res) {
        setTaskDate(formattedDate);
        setTask((prevTask) => ({ ...prevTask, date: formattedDate }));
        if (fetchTasks) {
          await fetchTasks();
        }
        toast.success('Fecha y hora de la tarea actualizadas correctamente');
      } else {
        console.error('updateTask returned falsy value:', res);
        toast.error('Error al actualizar la fecha y hora de la tarea');
      }
    } catch (error) {
      console.error('Error updating task date and time:', error);
      toast.error(`Error al actualizar la fecha y hora de la tarea: ${error.message}`);
    }
  };

  const handleTaskUpdate = async (updatedFields: Partial<Tasks>) => {
    try {
      // Update the task in the database
      await updateTask(task.id, updatedFields);

      // Update local state
      setTask((prevTask) => ({ ...prevTask, ...updatedFields }));

      if (updatedFields.category_id) {
        fetchCategory();
      }
      if (fetchTasks) {
        fetchTasks();
      }

      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleContactAsker = () => {
    if (asker?.phone_number) {
      const cleanedPhoneNumber = cleanPhoneNumber(asker.phone_number);
      const whatsappUrl = `https://wa.me/${cleanedPhoneNumber}`;
      window.open(whatsappUrl, '_blank');
    } else {
      toast.error('Número de teléfono del Asker no disponible');
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    const selectedCategory = allCategories.find((cat) => cat.id.toString() === categoryId);
    setCategory(selectedCategory || null);
    handleTaskUpdate({ category_id: parseInt(categoryId) });
  };

  const fetchTaskers = async (categoryId: number, cityToFilter: string) => {
    setIsTaskersLoading(true);
    try {
      const { data, error } = await fetchAvailableTaskers(categoryId, cityToFilter);

      if (error) throw error;
      setTaskers(data);
    } catch (error) {
      console.error('Error fetching taskers:', error);
      toast.error('Error al cargar taskers');
      setTaskers([]);
    } finally {
      setIsTaskersLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        onReset();
      }}
      size="5xl"
      scrollBehavior="inside"
      className="max-h-[90vh]"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex gap-3 items-center">
            <div>
              <h2 className="text-xl font-semibold">{category?.name || task?.category}</h2>
              <p className="text-sm text-gray-500">Task ID: {task?.id}</p>
            </div>
            <Button
              color="success"
              endContent={<FaWhatsapp />}
              onClick={handleContactAsker}
              className="text-white"
            >
              Contactar Asker
            </Button>
          </div>
          <div className="mt-4">
            <Select
              label="Categoría de la Task"
              placeholder="Seleccione una categoría"
              selectedKeys={category ? [category.id.toString()] : []}
              onSelectionChange={(keys) => handleCategoryChange(Array.from(keys)[0] as string)}
            >
              {allCategories.map((cat) => (
                <SelectItem key={cat.id.toString()} value={cat.id.toString()}>
                  {cat.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        </ModalHeader>
        <ModalBody className="overflow-y-auto">
          <div className="grid grid-cols-2 gap-8">
            {task && task.id && (
              <LeftColumn
                task={task}
                askerAddress={askerAddress}
                askerCommune={askerCommune}
                askerCity={askerCity}
                taskDate={taskDate}
                comments={comments}
                setIsDatePickerOpen={setIsDatePickerOpen}
                handleAddComment={handleAddComment}
                onTaskUpdate={handleTaskUpdate}
                category={category}
                taskers={taskers}
              />
            )}
            {task && task.id && (
              <RightColumn
                task={task}
                category={category}
                taskers={taskers}
                isTaskersLoading={isTaskersLoading}
              />
            )}
          </div>
        </ModalBody>
      </ModalContent>
      <ModalAddComment
        isOpen={isAddCommentModalOpen}
        onClose={() => setIsAddCommentModalOpen(false)}
        taskId={task?.id?.toString() || ''}
        userId={userProfile?.id?.toString() || ''}
        onCommentAdded={onCommentAdded}
      />
      <DatePicker
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onDateSelect={handleDateUpdate}
        currentDate={taskDate}
      />
    </Modal>
  );
};

export default ModalTask;
